import axios from 'axios';
import { CIDADES_ATENDIDAS } from 'data/cidadesAtendidas';

/**
 * Camada de dados dos municípios brasileiros. Sem React de propósito: o
 * SeletorCidade consome isto, mas os testes deste módulo rodam sem DOM.
 *
 * A regra que organiza o arquivo: a busca por texto NUNCA toca a rede. O dataset
 * inteiro vive em src/data/municipios.js e chega por import() dinâmico, então
 * digitar é uma operação local de sub-milissegundo. O único ponto que ainda fala
 * com o IBGE é buscarMunicipio(), e só quando recebe um código que o dataset
 * congelado não conhece — ver o comentário lá embaixo.
 */

const BASE_IBGE = 'https://servicodados.ibge.gov.br/api/v1/localidades';

// Código da UF = os 2 primeiros dígitos do código IBGE do município. É o que
// permite o dataset não gravar a sigla 5.571 vezes.
const UF_POR_CODIGO = {
	11: 'RO', 12: 'AC', 13: 'AM', 14: 'RR', 15: 'PA', 16: 'AP', 17: 'TO',
	21: 'MA', 22: 'PI', 23: 'CE', 24: 'RN', 25: 'PB', 26: 'PE', 27: 'AL', 28: 'SE', 29: 'BA',
	31: 'MG', 32: 'ES', 33: 'RJ', 35: 'SP',
	41: 'PR', 42: 'SC', 43: 'RS',
	50: 'MS', 51: 'MT', 52: 'GO', 53: 'DF',
};

export const UFS = [
	{ sigla: 'AC', nome: 'Acre' },
	{ sigla: 'AL', nome: 'Alagoas' },
	{ sigla: 'AP', nome: 'Amapá' },
	{ sigla: 'AM', nome: 'Amazonas' },
	{ sigla: 'BA', nome: 'Bahia' },
	{ sigla: 'CE', nome: 'Ceará' },
	{ sigla: 'DF', nome: 'Distrito Federal' },
	{ sigla: 'ES', nome: 'Espírito Santo' },
	{ sigla: 'GO', nome: 'Goiás' },
	{ sigla: 'MA', nome: 'Maranhão' },
	{ sigla: 'MT', nome: 'Mato Grosso' },
	{ sigla: 'MS', nome: 'Mato Grosso do Sul' },
	{ sigla: 'MG', nome: 'Minas Gerais' },
	{ sigla: 'PA', nome: 'Pará' },
	{ sigla: 'PB', nome: 'Paraíba' },
	{ sigla: 'PR', nome: 'Paraná' },
	{ sigla: 'PE', nome: 'Pernambuco' },
	{ sigla: 'PI', nome: 'Piauí' },
	{ sigla: 'RJ', nome: 'Rio de Janeiro' },
	{ sigla: 'RN', nome: 'Rio Grande do Norte' },
	{ sigla: 'RS', nome: 'Rio Grande do Sul' },
	{ sigla: 'RO', nome: 'Rondônia' },
	{ sigla: 'RR', nome: 'Roraima' },
	{ sigla: 'SC', nome: 'Santa Catarina' },
	{ sigla: 'SP', nome: 'São Paulo' },
	{ sigla: 'SE', nome: 'Sergipe' },
	{ sigla: 'TO', nome: 'Tocantins' },
];

const SIGLAS = new Set(UFS.map((u) => u.sigla));

/** '4119905' -> 'PR'. Síncrono e sem dataset: só olha os 2 primeiros dígitos. */
export function ufDoCodigo(codigo) {
	if (!codigo) return '';
	return UF_POR_CODIGO[String(codigo).slice(0, 2)] || '';
}

/** 'Paraná' -> 'PR'. Existe porque o ViaCEP nem sempre devolve o campo `uf`. */
export function siglaPorNomeUf(nome) {
	if (!nome) return '';
	const alvo = normalizar(nome);
	const achado = UFS.find((u) => normalizar(u.nome) === alvo || u.sigla === String(nome).toUpperCase());
	return achado ? achado.sigla : '';
}

/**
 * ÚNICO lugar do projeto que monta a string "Nome - UF". Antes ela era montada
 * por template em três arquivos independentes, e o descompasso entre eles é o que
 * produzia o `"- PR"` gravado no banco quando o nome chegava vazio.
 */
export function formatarRotulo(municipio) {
	if (!municipio || !municipio.nome || !municipio.uf) return '';
	return `${municipio.nome} - ${municipio.uf}`;
}

/**
 * Tira acento, caixa e pontuação para a comparação.
 *
 * A ordem importa: os apóstrofos saem ANTES do colapso de não-alfanuméricos, para
 * que "Santa Bárbara d'Oeste" vire "santa barbara doeste" e case com quem digita
 * "doeste". Se o apóstrofo virasse espaço, só "d oeste" acharia.
 */
export function normalizar(texto) {
	return String(texto || '')
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/['´`’.]/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();
}

function montarMunicipio(codigo, nome, porte = 0) {
	const uf = ufDoCodigo(codigo);
	return { codigo: String(codigo), nome, uf, porte, rotulo: nome && uf ? `${nome} - ${uf}` : '' };
}

let promessaIndice = null;

/**
 * Carrega e indexa o dataset uma vez por sessão.
 *
 * O import() é dinâmico para o webpack pôr os ~116 KB num chunk separado: só
 * quem abre uma tela com seletor de cidade paga o download, e o main.js não
 * engorda para o site inteiro. Normalizar os 5.571 nomes custa poucos
 * milissegundos e acontece aqui dentro, uma vez, fora do caminho de digitação.
 */
export function carregarIndice() {
	if (!promessaIndice) {
		promessaIndice = import('data/municipios').then(({ LINHAS }) => {
			const lista = [];
			const porCodigo = new Map();

			for (const linha of LINHAS.split('\n')) {
				const codigo = linha.slice(0, 7);
				const porte = Number(linha.slice(7, 8));
				const nome = linha.slice(8);
				const municipio = montarMunicipio(codigo, nome, porte);
				municipio.busca = normalizar(nome);
				lista.push(municipio);
				porCodigo.set(codigo, municipio);
			}

			return { lista, porCodigo };
		});
	}
	return promessaIndice;
}

const extras = new Map();

/**
 * Resolve um código IBGE isolado — o caso de valor que chega do JWT, de um cookie
 * ou do banco, sem o nome junto.
 *
 * É o ÚNICO ponto do módulo que pode ir à rede, e só quando o código não está no
 * dataset congelado: um município criado depois da geração do arquivo. São poucas
 * linhas que cobrem a dessincronia sem colocar uma requisição em cada tecla
 * digitada, que é o que a busca por texto jamais pode ter.
 */
export async function buscarMunicipio(codigo) {
	if (!codigo) return null;
	const chave = String(codigo);

	const { porCodigo } = await carregarIndice();
	const achado = porCodigo.get(chave);
	if (achado) return achado;

	if (extras.has(chave)) return extras.get(chave);

	try {
		const resposta = await axios.get(`${BASE_IBGE}/municipios/${chave}`);
		const nome = resposta?.data?.nome;
		const municipio = nome ? montarMunicipio(chave, nome) : null;
		extras.set(chave, municipio);
		return municipio;
	} catch (erro) {
		// Não memoiza a falha: uma queda momentânea do IBGE não pode condenar o
		// código a nunca mais resolver enquanto a aba estiver aberta.
		return null;
	}
}

/**
 * 'Jacarezinho', 'PR' -> 'jacarezinho-pr'. O pedaço de URL das páginas por cidade
 * (/diaristas/:slug, pages/Cidade.jsx).
 *
 * A UF vai no fim porque o nome sozinho não é único ("Bom Jesus" existe em oito
 * estados); nome + UF é. A mesma regra está copiada em scripts/gerar-sitemap.js,
 * que roda em Node sem o alias de import do CRA, e o services/sitemap.test.js
 * falha se as duas divergirem.
 */
export function slugDoMunicipio(municipio) {
	if (!municipio || !municipio.nome || !municipio.uf) return '';
	return `${normalizar(municipio.nome).replace(/ /g, '-')}-${municipio.uf.toLowerCase()}`;
}

let promessaPorSlug = null;

/** O inverso de slugDoMunicipio, ou null quando o slug não é de município nenhum. */
export async function buscarMunicipioPorSlug(slug) {
	if (!slug) return null;
	if (!promessaPorSlug) {
		promessaPorSlug = carregarIndice().then(({ lista }) =>
			new Map(lista.map((municipio) => [slugDoMunicipio(municipio), municipio]))
		);
	}
	const porSlug = await promessaPorSlug;
	return porSlug.get(String(slug).toLowerCase()) || null;
}

/** Porte 4 = 10 mil habitantes ou mais (o porte é floor(log10(população))). */
export const PORTE_MINIMO_DESTAQUE = 4;

const ATENDIDAS = new Set(CIDADES_ATENDIDAS);

/** Se a cidade tem profissional na busca, segundo data/cidadesAtendidas. */
export function cidadeAtendida(municipio) {
	return Boolean(municipio) && ATENDIDAS.has(String(municipio.codigo));
}

let promessaDestaque = null;

/**
 * As cidades em destaque: 10 mil habitantes ou mais, ou com profissional na busca
 * (data/cidadesAtendidas). Em ordem de UF e nome.
 *
 * São as páginas por cidade que o site oferece ao Google: o sitemap, a página /diaristas e o bloco
 * "Outras cidades" da página da cidade. As outras 2,4 mil abrem, mas ficam de fora
 * (prompts/indexacao-paginas-por-cidade.md). A mesma regra está copiada em scripts/gerar-sitemap.js,
 * e o services/sitemap.test.js falha se as duas divergirem.
 */
export function listarCidadesEmDestaque() {
	if (!promessaDestaque) {
		promessaDestaque = carregarIndice().then(({ lista }) =>
			lista
				.filter((municipio) => municipio.porte >= PORTE_MINIMO_DESTAQUE || cidadeAtendida(municipio))
				.sort((a, b) => a.uf.localeCompare(b.uf) || a.nome.localeCompare(b.nome, 'pt-BR'))
		);
	}
	return promessaDestaque;
}

/**
 * Todos os municípios de uma UF, em ordem alfabética — o que alimenta um <select>
 * de cidade depois que a pessoa escolheu o estado (o cadastro, pages/Usuario/Novo.jsx).
 *
 * Existe para aquele par de selects parar de ir ao IBGE: sem rede a lista chegava
 * vazia, o campo sumia da tela e a etapa travava num erro que ninguém via. Aqui a
 * resposta sai do dataset congelado, que no app instalado está dentro do bundle.
 * Vale a mesma ressalva de sempre: município criado depois da geração não aparece.
 */
export async function listarMunicipiosDaUf(uf) {
	const sigla = String(uf || '').toUpperCase();
	if (!SIGLAS.has(sigla)) return [];

	const { lista } = await carregarIndice();
	return lista
		.filter((municipio) => municipio.uf === sigla)
		.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/**
 * Separa um sufixo de UF da consulta: "curitiba pr", "curitiba - pr" e
 * "curitiba/pr" viram { termo: 'curitiba', uf: 'PR' }.
 *
 * É a forma natural de desempatar homônimo — "Bom Jesus" existe em oito estados —
 * e substitui com folga o select de estado que saiu da tela.
 */
function separarUf(consultaNormalizada) {
	const casou = /^(.*[a-z0-9])\s+([a-z]{2})$/.exec(consultaNormalizada);
	if (!casou) return { termo: consultaNormalizada, uf: '' };

	const sigla = casou[2].toUpperCase();
	if (!SIGLAS.has(sigla)) return { termo: consultaNormalizada, uf: '' };

	return { termo: casou[1].trim(), uf: sigla };
}

export const MINIMO_CARACTERES = 2;

/**
 * Busca por nome. Devolve { itens, total } — o total é o número real de
 * casamentos, para a interface poder dizer "47 encontradas, mostrando 8" e a
 * pessoa saber que refinar adianta.
 *
 * Ranking por baldes, sem sort global: 0 = o nome começa com o termo, 1 = alguma
 * palavra do nome começa com o termo, 2 = aparece em qualquer posição. Dentro do
 * balde: UF preferida primeiro, depois o porte (faixa de população), depois ordem
 * alfabética. O porte é o que faz "curi" oferecer Curitiba e não Curimatá — quem
 * digita um prefixo curto quase sempre quer a cidade grande, e a ordem alfabética
 * pura entrega justamente a menos provável.
 *
 * Sem debounce e sem estrutura de índice: são 5.571 indexOf sobre strings de ~15
 * caracteres, algo entre 0,2 e 0,5 ms. O orçamento de um quadro é 16 ms.
 */
export async function buscarMunicipios(consulta, opcoes = {}) {
	const { limite = 8, ufPreferida = '' } = opcoes;

	const normalizada = normalizar(consulta);
	if (normalizada.length < MINIMO_CARACTERES) return { itens: [], total: 0 };

	const { lista } = await carregarIndice();

	const coletar = (termo, ufFiltro) => {
		const baldes = [[], [], []];
		for (const municipio of lista) {
			if (ufFiltro && municipio.uf !== ufFiltro) continue;

			const posicao = municipio.busca.indexOf(termo);
			if (posicao === -1) continue;

			if (posicao === 0) baldes[0].push(municipio);
			else if (municipio.busca[posicao - 1] === ' ') baldes[1].push(municipio);
			else baldes[2].push(municipio);
		}
		return baldes;
	};

	// A leitura literal vem primeiro, e o sufixo de UF só entra quando ela não acha
	// nada. Sem essa ordem, "sao pa" — alguém no meio de digitar "São Paulo" — seria
	// lido como "sao" no Pará e a capital sumiria da lista. Já "curitiba pr" e "bom
	// jesus pi" não casam com nome nenhum, então caem na leitura com UF, que é
	// exatamente onde ela serve.
	let baldes = coletar(normalizada, '');
	if (baldes[0].length + baldes[1].length + baldes[2].length === 0) {
		const { termo, uf } = separarUf(normalizada);
		if (termo && uf) baldes = coletar(termo, uf);
	}

	const preferida = ufPreferida || '';
	const ordenar = (a, b) => {
		if (preferida) {
			const pa = a.uf === preferida ? 0 : 1;
			const pb = b.uf === preferida ? 0 : 1;
			if (pa !== pb) return pa - pb;
		}
		if (a.porte !== b.porte) return b.porte - a.porte;
		return a.nome.localeCompare(b.nome, 'pt-BR');
	};

	const total = baldes[0].length + baldes[1].length + baldes[2].length;
	const itens = [];
	for (const balde of baldes) {
		if (itens.length >= limite) break;
		balde.sort(ordenar);
		for (const municipio of balde) {
			if (itens.length >= limite) break;
			itens.push(municipio);
		}
	}

	return { itens, total };
}
